import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Skeleton,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { PlusOutlined, SendOutlined } from '@ant-design/icons';
import type {
  AudienceFilter,
  BroadcastChannel,
  CreateBroadcastRequest,
  CreateTemplateRequest,
  TemplatePurpose,
  TemplateRow,
} from '@velora/contracts';
import { ApiError, productApi } from '../../../api';

/**
 * Admin surface — Broadcast composer.
 * Pick audience → preview count → compose message → send (dev stub records
 * "would deliver" without hitting a real gateway) or save as draft.
 */
export function BroadcastComposerPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [templateOpen, setTemplateOpen] = useState(false);

  const [scope, setScope] = useState<AudienceFilter['scope']>('all');
  const [grade, setGrade] = useState<string>('');
  const [section, setSection] = useState<string>('');
  const [admissionNumber, setAdmissionNumber] = useState<string>('');
  const [channel, setChannel] = useState<BroadcastChannel>('sms');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<string | undefined>();

  const filter: AudienceFilter = {
    scope,
    grade: scope === 'grade' || scope === 'section' ? grade || undefined : undefined,
    section: scope === 'section' ? section || undefined : undefined,
    admission_number: scope === 'student' ? admissionNumber || undefined : undefined,
  };

  const preview = useQuery({
    queryKey: ['audience-preview', institution, JSON.stringify(filter)],
    queryFn: () => productApi.previewAudience(institution, filter),
    enabled: !!institution && shouldPreview(filter),
  });

  const templates = useQuery({
    queryKey: ['templates', institution],
    queryFn: () => productApi.listTemplates(institution),
    enabled: !!institution,
  });

  const history = useQuery({
    queryKey: ['broadcasts', institution],
    queryFn: () => productApi.listBroadcasts(institution),
    enabled: !!institution,
  });

  useEffect(() => {
    if (!selectedTemplate) return;
    const t = templates.data?.rows.find((x) => x.id === selectedTemplate);
    if (t) {
      setSubject((s) => s || t.name);
      setBody(t.body);
    }
  }, [selectedTemplate, templates.data]);

  const send = useMutation({
    mutationFn: (send_now: boolean) => {
      const input: CreateBroadcastRequest = {
        subject: subject.trim(), body: body.trim(), channel, filter, send_now,
      };
      return productApi.createBroadcast(institution, input);
    },
    onSuccess: (r, send_now) => {
      void message.success(send_now ? `Sent to ${r.recipient_count} recipient${r.recipient_count === 1 ? '' : 's'} (dev stub)` : 'Saved as draft');
      setSubject(''); setBody(''); setSelectedTemplate(undefined);
      void qc.invalidateQueries({ queryKey: ['broadcasts', institution] });
    },
    onError: (e) => void message.error(errMsg(e) ?? 'Send failed'),
  });

  const canSend = subject.trim().length > 0 && body.trim().length > 0 && (preview.data?.unique_phones ?? 0) > 0;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>Broadcasts</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            Compose → filter audience → preview → send. Delivery via SMS/WhatsApp gateway lands
            with the provider integration — the dev stub records "would deliver" per recipient.
          </Typography.Text>
        </div>
        <Space>
          <Button icon={<PlusOutlined />} onClick={() => setTemplateOpen(true)}>Add template</Button>
        </Space>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)', gap: 12, alignItems: 'flex-start' }}>
        <Card size="small" title="Compose">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <Field label="Audience">
                <Select
                  size="small"
                  value={scope}
                  onChange={(v) => setScope(v)}
                  style={{ width: '100%' }}
                  options={[
                    { value: 'all', label: 'All enrolled guardians' },
                    { value: 'grade', label: 'By grade' },
                    { value: 'section', label: 'By grade + section' },
                    { value: 'student', label: 'One student' },
                  ]}
                />
              </Field>
              {(scope === 'grade' || scope === 'section') && (
                <Field label="Grade">
                  <Input size="small" value={grade} onChange={(e) => setGrade(e.target.value)} placeholder="9" />
                </Field>
              )}
              {scope === 'section' && (
                <Field label="Section">
                  <Input size="small" value={section} onChange={(e) => setSection(e.target.value)} placeholder="A" />
                </Field>
              )}
              {scope === 'student' && (
                <Field label="Admission no.">
                  <Input size="small" value={admissionNumber} onChange={(e) => setAdmissionNumber(e.target.value)} placeholder="2025-0134" />
                </Field>
              )}
            </div>
            <div>
              <Field label="Channel">
                <Select
                  size="small" value={channel} onChange={(v) => setChannel(v)} style={{ width: '100%' }}
                  options={[
                    { value: 'sms', label: 'SMS' },
                    { value: 'whatsapp', label: 'WhatsApp' },
                    { value: 'app', label: 'App notification' },
                    { value: 'email', label: 'Email' },
                  ]}
                />
              </Field>
              <Field label="Template (optional)">
                <Select
                  size="small" value={selectedTemplate} onChange={setSelectedTemplate}
                  placeholder="Pick to prefill subject + body"
                  style={{ width: '100%' }} allowClear
                  options={(templates.data?.rows ?? []).map((t) => ({ value: t.id, label: `${t.name} · ${t.purpose}` }))}
                />
              </Field>
            </div>
          </div>

          <Field label="Subject">
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Fee reminder · Term 2" />
          </Field>
          <Field label="Message">
            <Input.TextArea rows={5} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type the message body — {{tokens}} land in a later iteration." />
          </Field>

          <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
            <Button onClick={() => send.mutate(false)} loading={send.isPending && send.variables === false} disabled={!canSend}>
              Save as draft
            </Button>
            <Popconfirm
              title={`Send to ${preview.data?.unique_phones ?? 0} phones?`}
              description="Dev stub — no real gateway. Recipients marked 'delivered' in the audit trail."
              onConfirm={() => send.mutate(true)}
              okText="Send"
              disabled={!canSend}
            >
              <Button type="primary" icon={<SendOutlined />} disabled={!canSend}>
                Send now
              </Button>
            </Popconfirm>
          </div>
        </Card>

        <Card size="small" title="Audience preview">
          {!shouldPreview(filter) && (
            <Typography.Text type="secondary">Pick a scope to preview recipients.</Typography.Text>
          )}
          {preview.isLoading && <Skeleton active paragraph={{ rows: 3 }} />}
          {preview.data && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
                <Statistic title="Students" value={preview.data.matched_students} />
                <Statistic title="Guardians" value={preview.data.matched_guardians} />
                <Statistic title="Phones" value={preview.data.unique_phones} />
              </div>
              <Typography.Text type="secondary" style={{ fontSize: 11.5, display: 'block', marginTop: 10 }}>
                Sample (first 8, C-02 fan-out — one per family):
              </Typography.Text>
              {preview.data.sample.length === 0 ? (
                <Empty style={{ padding: 8 }} description="No phones matched" />
              ) : (
                <div style={{ marginTop: 6 }}>
                  {preview.data.sample.map((s) => (
                    <div key={s.phone} style={{ padding: '6px 0', borderTop: '1px solid #f1f1f4', fontSize: 12 }}>
                      <div><b>{s.guardian_name}</b> <span style={{ color: '#8a94a6', fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>· {s.phone}</span></div>
                      <div style={{ color: '#5b6478' }}>{s.ward_names.join(', ')}</div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      <Card size="small" title="History">
        {history.isLoading && <Skeleton active paragraph={{ rows: 3 }} />}
        {history.data && history.data.rows.length === 0 && (
          <Empty description="No broadcasts yet." style={{ padding: 20 }} />
        )}
        {history.data && history.data.rows.length > 0 && (
          <Table
            size="small"
            pagination={{ pageSize: 8 }}
            rowKey="id"
            dataSource={history.data.rows}
            columns={[
              { title: 'When', dataIndex: 'created_at', key: 'w', width: 170, render: (v: string) => new Date(v).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) },
              { title: 'Subject', dataIndex: 'subject', key: 's' },
              { title: 'Channel', dataIndex: 'channel', key: 'c', width: 100, render: (v: string) => <Tag>{v}</Tag> },
              { title: 'Recipients', dataIndex: 'recipient_count', key: 'r', width: 100, align: 'right' },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'st',
                width: 100,
                render: (v: string) => <Tag color={v === 'sent' ? 'success' : v === 'draft' ? 'default' : v === 'failed' ? 'error' : 'processing'}>{v}</Tag>,
              },
            ]}
          />
        )}
      </Card>

      <AddTemplateModal
        open={templateOpen}
        onClose={() => setTemplateOpen(false)}
        institution={institution}
        onCreated={() => {
          setTemplateOpen(false);
          void qc.invalidateQueries({ queryKey: ['templates', institution] });
          void message.success('Template added');
        }}
      />
    </Space>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, color: '#8a94a6', marginBottom: 4, letterSpacing: 0.3, textTransform: 'uppercase', fontWeight: 600 }}>{label}</div>
      {children}
    </div>
  );
}

function AddTemplateModal({
  open, onClose, institution, onCreated,
}: { open: boolean; onClose: () => void; institution: string; onCreated: () => void }) {
  const [form] = Form.useForm<{ name: string; purpose: TemplatePurpose; body: string }>();
  const m = useMutation({
    mutationFn: (v: CreateTemplateRequest) => productApi.createTemplate(institution, v),
    onSuccess: () => { form.resetFields(); onCreated(); },
  });
  const err = errMsg(m.error);
  return (
    <Modal open={open} title="Add template" onCancel={() => { m.reset(); onClose(); }} okText="Add" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose>
      <Form form={form} layout="vertical" initialValues={{ purpose: 'general' }} onFinish={(v) => m.mutate({ name: v.name.trim(), purpose: v.purpose, body: v.body })}>
        <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input placeholder="Fee reminder · Term 2" autoFocus /></Form.Item>
        <Form.Item name="purpose" label="Purpose" rules={[{ required: true }]}>
          <Select options={[
            { value: 'general', label: 'General' },
            { value: 'fee_reminder', label: 'Fee reminder' },
            { value: 'attendance', label: 'Attendance' },
            { value: 'result', label: 'Result' },
            { value: 'event', label: 'Event' },
            { value: 'emergency', label: 'Emergency' },
          ]} />
        </Form.Item>
        <Form.Item name="body" label="Body" rules={[{ required: true }]}>
          <Input.TextArea rows={5} placeholder="Dear {{guardian_name}}, your ward {{student_name}}..." />
        </Form.Item>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}

function shouldPreview(f: AudienceFilter): boolean {
  if (f.scope === 'all') return true;
  if (f.scope === 'grade') return !!f.grade;
  if (f.scope === 'section') return !!f.grade;
  if (f.scope === 'student') return !!f.admission_number;
  return false;
}

function errMsg(err: unknown): string | null {
  if (!err) return null;
  const body = err instanceof ApiError ? (err.body as { message?: string } | null) : null;
  return body?.message ?? (err instanceof Error ? err.message : null);
}
